export const QUESTION_TYPES = [
  { value: "likert", label: "Likert" },
  { value: "single_choice", label: "Single choice" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "yes_no", label: "Yes / No" },
  { value: "dropdown", label: "Dropdown" },
  { value: "slider", label: "Slider" },
  { value: "numeric", label: "Numeric" },
  { value: "text", label: "Text" },
  { value: "matrix", label: "Matrix" },
  { value: "forced_choice", label: "Forced choice (most / least)" },
];

export const usesOptions = (questionType) => !["text", "numeric", "slider", "forced_choice"].includes(questionType);

export const usesRows = (questionType) => questionType === "matrix" || questionType === "forced_choice";

const defaultId = () => crypto.randomUUID();

const deepClone = (value) => {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

const collectIds = (questions) => {
  const ids = new Set();
  (questions || []).forEach((question) => {
    if (question.id) ids.add(question.id);
    (question.options || []).forEach((option) => option.id && ids.add(option.id));
    (question.matrix_rows || []).forEach((row) => row.id && ids.add(row.id));
  });
  return ids;
};

const idAllocator = (questions, createId = defaultId) => {
  const used = collectIds(questions);
  return () => {
    for (let attempt = 0; attempt < 100; attempt += 1) {
      const candidate = createId();
      if (candidate && !used.has(candidate)) {
        used.add(candidate);
        return candidate;
      }
    }
    throw new Error("Unable to generate a unique builder identifier.");
  };
};

export const nextCode = (items, prefix, minimumWidth = 1) => {
  const used = new Set((items || []).map((item) => String(item.code || "").toLowerCase()));
  const pattern = new RegExp(`^${prefix}(\\d+)$`, "i");
  let greatest = 0;
  let width = minimumWidth;

  (items || []).forEach((item) => {
    const match = String(item.code || "").match(pattern);
    if (!match) return;
    greatest = Math.max(greatest, Number(match[1]));
    width = Math.max(width, match[1].length);
  });

  let number = greatest + 1;
  let code = `${prefix}${String(number).padStart(width, "0")}`;
  while (used.has(code.toLowerCase())) {
    number += 1;
    code = `${prefix}${String(number).padStart(width, "0")}`;
  }
  return code;
};

export const normalizeQuestionOrder = (questions) => (
  (questions || []).map((question, index) => ({ ...question, order: index + 1 }))
);

const normalizeNestedOrder = (items) => (
  (items || []).map((item, index) => ({ ...item, order: index + 1 }))
);

export const localizedPatch = (language, field, value) => ({ [`${field}_${language}`]: value });

export const updateQuestionAt = (questions, index, patch) => (
  questions.map((question, questionIndex) => (
    questionIndex === index ? { ...question, ...patch } : question
  ))
);

export const appendQuestion = (questions, sectionId = null, createId = defaultId) => {
  const allocateId = idAllocator(questions, createId);
  const question = {
    id: allocateId(),
    order: questions.length + 1,
    code: nextCode(questions, "Q", 2),
    question_type: "likert",
    text_id: "",
    text_en: "",
    description_id: "",
    description_en: "",
    required: true,
    reverse_scored: false,
    section_id: sectionId,
    dimension_ids: [],
    options: [],
    matrix_rows: [],
    slider_min: 0,
    slider_max: 10,
    slider_step: 1,
  };
  return normalizeQuestionOrder([...questions, question]);
};

const yesNoOptions = (allocateId) => [
  { id: allocateId(), order: 1, code: "Y", label_id: "Ya", label_en: "Yes", score: 1 },
  { id: allocateId(), order: 2, code: "N", label_id: "Tidak", label_en: "No", score: 0 },
];

export const changeQuestionTypeAt = (questions, index, questionType, createId = defaultId) => {
  const question = questions[index];
  if (!question) return questions;

  const allocateId = idAllocator(questions, createId);
  const changed = { ...question, question_type: questionType };
  if (questionType === "yes_no" && !(changed.options || []).length) {
    changed.options = yesNoOptions(allocateId);
  }
  if (questionType === "matrix" && !(changed.matrix_rows || []).length) {
    changed.matrix_rows = [{
      id: allocateId(), order: 1, code: "R1", label_id: "", label_en: "",
      dimension_ids: [], reverse_scored: false,
    }];
  }
  if (questionType === "forced_choice" && (changed.matrix_rows || []).length < 2) {
    const rows = [...(changed.matrix_rows || [])];
    while (rows.length < 3) {
      rows.push({
        id: allocateId(), order: rows.length + 1, code: `S${rows.length + 1}`, label_id: "", label_en: "",
        dimension_ids: [], reverse_scored: false,
      });
    }
    changed.matrix_rows = rows;
  }
  return updateQuestionAt(questions, index, changed);
};

export const addOptionAt = (questions, questionIndex, createId = defaultId) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const options = question.options || [];
  const allocateId = idAllocator(questions, createId);
  const option = {
    id: allocateId(),
    order: options.length + 1,
    code: nextCode(options, "O"),
    label_id: "",
    label_en: "",
    score: 0,
  };
  return updateQuestionAt(questions, questionIndex, { options: [...options, option] });
};

export const updateOptionAt = (questions, questionIndex, optionIndex, patch) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const options = (question.options || []).map((option, index) => (
    index === optionIndex ? { ...option, ...patch } : option
  ));
  return updateQuestionAt(questions, questionIndex, { options });
};

export const removeOptionAt = (questions, questionIndex, optionIndex) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const options = normalizeNestedOrder((question.options || []).filter((_, index) => index !== optionIndex));
  return updateQuestionAt(questions, questionIndex, { options });
};

export const moveOptionAt = (questions, questionIndex, optionIndex, direction) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const options = [...(question.options || [])];
  const targetIndex = optionIndex + direction;
  if (targetIndex < 0 || targetIndex >= options.length) return questions;
  const [moved] = options.splice(optionIndex, 1);
  options.splice(targetIndex, 0, moved);
  return updateQuestionAt(questions, questionIndex, { options: normalizeNestedOrder(options) });
};

export const addMatrixRowAt = (questions, questionIndex, createId = defaultId) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const rows = question.matrix_rows || [];
  const allocateId = idAllocator(questions, createId);
  const row = {
    id: allocateId(),
    order: rows.length + 1,
    code: nextCode(rows, "R"),
    label_id: "",
    label_en: "",
    dimension_ids: [],
    reverse_scored: false,
  };
  return updateQuestionAt(questions, questionIndex, { matrix_rows: [...rows, row] });
};

export const updateMatrixRowAt = (questions, questionIndex, rowIndex, patch) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const rows = (question.matrix_rows || []).map((row, index) => (
    index === rowIndex ? { ...row, ...patch } : row
  ));
  return updateQuestionAt(questions, questionIndex, { matrix_rows: rows });
};

export const removeMatrixRowAt = (questions, questionIndex, rowIndex) => {
  const question = questions[questionIndex];
  if (!question) return questions;
  const rows = normalizeNestedOrder((question.matrix_rows || []).filter((_, index) => index !== rowIndex));
  return updateQuestionAt(questions, questionIndex, { matrix_rows: rows });
};

export const duplicateQuestionAt = (questions, questionIndex, createId = defaultId) => {
  const source = questions[questionIndex];
  if (!source) return questions;

  const allocateId = idAllocator(questions, createId);
  const duplicate = deepClone(source);
  duplicate.id = allocateId();
  duplicate.code = nextCode(questions, "Q", 2);
  duplicate.options = (duplicate.options || []).map((option) => ({ ...option, id: allocateId() }));
  duplicate.matrix_rows = (duplicate.matrix_rows || []).map((row) => ({ ...row, id: allocateId() }));

  const result = [...questions];
  result.splice(questionIndex + 1, 0, duplicate);
  return normalizeQuestionOrder(result);
};

export const removeQuestionAt = (questions, questionIndex) => (
  normalizeQuestionOrder(questions.filter((_, index) => index !== questionIndex))
);

export const reorderQuestions = (questions, oldIndex, newIndex) => {
  if (oldIndex < 0 || newIndex < 0 || oldIndex >= questions.length || newIndex >= questions.length) return questions;
  const reordered = [...questions];
  const [moved] = reordered.splice(oldIndex, 1);
  reordered.splice(newIndex, 0, moved);
  return normalizeQuestionOrder(reordered);
};

export const reorderQuestionsWithinSection = (questions, activeId, overId) => {
  const active = (questions || []).find((question) => question.id === activeId);
  const over = (questions || []).find((question) => question.id === overId);
  if (!active || !over || (active.section_id || null) !== (over.section_id || null)) return questions;

  const positions = [];
  const sectionQuestions = [];
  questions.forEach((question, index) => {
    if ((question.section_id || null) === (active.section_id || null)) {
      positions.push(index);
      sectionQuestions.push(question);
    }
  });
  const oldIndex = sectionQuestions.findIndex((question) => question.id === activeId);
  const newIndex = sectionQuestions.findIndex((question) => question.id === overId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return questions;

  const reordered = [...sectionQuestions];
  const [moved] = reordered.splice(oldIndex, 1);
  reordered.splice(newIndex, 0, moved);
  const result = [...questions];
  positions.forEach((position, index) => { result[position] = reordered[index]; });
  return normalizeQuestionOrder(result);
};

export const normalizeSectionOrder = (sections) => (
  (sections || []).map((section, index) => ({ ...section, order: index + 1 }))
);

export const appendSection = (sections, createId = defaultId) => {
  const used = new Set((sections || []).map((section) => section.id).filter(Boolean));
  let id = null;
  for (let attempt = 0; attempt < 100; attempt += 1) {
    const candidate = createId();
    if (candidate && !used.has(candidate)) {
      id = candidate;
      break;
    }
  }
  if (!id) throw new Error("Unable to generate a unique Section identifier.");
  return normalizeSectionOrder([...(sections || []), {
    id,
    order: (sections || []).length + 1,
    title_id: "",
    title_en: "",
    description_id: "",
    description_en: "",
  }]);
};

export const updateSectionAt = (sections, index, patch) => (
  (sections || []).map((section, sectionIndex) => (
    sectionIndex === index ? { ...section, ...patch } : section
  ))
);

export const moveSectionAt = (sections, index, direction) => {
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || index >= sections.length || targetIndex >= sections.length) return sections;
  const reordered = [...sections];
  const [moved] = reordered.splice(index, 1);
  reordered.splice(targetIndex, 0, moved);
  return normalizeSectionOrder(reordered);
};

export const removeSectionAt = (sections, questions, index) => {
  const removed = (sections || [])[index];
  if (!removed) return { sections, questions };
  return {
    sections: normalizeSectionOrder(sections.filter((_, sectionIndex) => sectionIndex !== index)),
    questions: (questions || []).map((question) => (
      question.section_id === removed.id ? { ...question, section_id: null } : question
    )),
  };
};

export const groupQuestionsBySection = (questions, sections) => {
  const orderedQuestions = [...(questions || [])].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const orderedSections = [...(sections || [])].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  const knownSectionIds = new Set(orderedSections.map((section) => section.id));
  return [
    ...orderedSections.map((section) => ({
      id: section.id,
      section,
      questions: orderedQuestions.filter((question) => question.section_id === section.id),
    })),
    {
      id: "__unassigned__",
      section: null,
      questions: orderedQuestions.filter((question) => !question.section_id || !knownSectionIds.has(question.section_id)),
    },
  ];
};

export const removeDimensionFromQuestions = (questions, dimensionId) => (
  questions.map((question) => ({
    ...question,
    dimension_ids: (question.dimension_ids || []).filter((id) => id !== dimensionId),
    matrix_rows: (question.matrix_rows || []).map((row) => ({
      ...row,
      dimension_ids: (row.dimension_ids || []).filter((id) => id !== dimensionId),
    })),
  }))
);

export const mergeInstrumentForEditing = (defaults, instrument) => ({
  ...defaults,
  ...instrument,
  scoring: { ...defaults.scoring, ...(instrument.scoring || {}) },
  result_template: { ...defaults.result_template, ...(instrument.result_template || {}) },
});
